const INIT_FLAG = 'eventsViewBound';
const STORAGE_KEY = 'events-calendar-view';
type EventsView = 'list' | 'timeline';

function isEventsView(value: string | null | undefined): value is EventsView {
  return value === 'list' || value === 'timeline';
}

function readStoredView(): EventsView {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isEventsView(stored)) return stored;
  } catch {
    // ignore storage errors
  }
  return 'list';
}

function writeStoredView(view: EventsView): void {
  try {
    localStorage.setItem(STORAGE_KEY, view);
  } catch {
    // ignore storage errors
  }
}

function readViewFromUrl(): EventsView | null {
  const param = new URLSearchParams(window.location.search).get('view');
  return isEventsView(param) ? param : null;
}

function syncViewToUrl(view: EventsView): void {
  const params = new URLSearchParams(window.location.search);
  if (view === 'list') {
    params.delete('view');
  } else {
    params.set('view', view);
  }
  const queryString = params.toString();
  const nextUrl = queryString
    ? `${window.location.pathname}?${queryString}`
    : window.location.pathname;
  window.history.replaceState(null, '', nextUrl);
}

export function initEventsCalendarView(root?: ParentNode): void {
  const scope = root ?? document;
  const list = scope.querySelector<HTMLElement>('[data-events-list]');
  const switcher = scope.querySelector<HTMLElement>('[data-events-view-switcher]');

  if (!list || !switcher) return;
  if (switcher.dataset[INIT_FLAG] === 'true') return;
  switcher.dataset[INIT_FLAG] = 'true';

  const buttons = Array.from(
    switcher.querySelectorAll<HTMLButtonElement>('[data-events-view]'),
  );

  const applyView = (view: EventsView, persist = true): void => {
    list.dataset.eventsView = view;

    buttons.forEach((button) => {
      const isActive = button.dataset.eventsView === view;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    if (persist) {
      writeStoredView(view);
      syncViewToUrl(view);
    }
  };

  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      const view = button.dataset.eventsView;
      if (!isEventsView(view) || view === list.dataset.eventsView) return;
      applyView(view);
    });
  });

  const initialView = readViewFromUrl() ?? readStoredView();
  applyView(initialView, false);
  writeStoredView(initialView);
  syncViewToUrl(initialView);
}
